import React, { useState, useEffect, useContext } from 'react';
import { ProjectContext } from '../context/project_context';
import TimecodeInput from './TimecodeInput';

/**
 * InsertRowModal - Modal for inserting a new transcript row
 * 
 * Props:
 * - isOpen: boolean - whether the modal is open
 * - onClose: function - called when modal should close
 * - onInsert: function(startTime, endTime, text) - called when insert is confirmed
 * - defaultStartTime: number - default start time in seconds
 * - defaultEndTime: number - default end time in seconds
 * - type: 'transcript' | 'english_transcript' - type of transcript to insert
 */
const InsertRowModal = ({ 
  isOpen, 
  onClose, 
  onInsert,
  defaultStartTime = 0, 
  defaultEndTime = 1,
  type = 'transcript'
}) => {
  const { formatTimeCode, currentPlaybackTime } = useContext(ProjectContext);
  
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartTime(defaultStartTime || currentPlaybackTime || 0);
      setEndTime(defaultEndTime || (currentPlaybackTime ? currentPlaybackTime + 2 : 2));
      setText('');
      setError('');
    }
  }, [isOpen, defaultStartTime, defaultEndTime, currentPlaybackTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (endTime <= startTime) {
      setError('End time must be greater than start time');
      return;
    }
    
    if (endTime - startTime < 0.1) {
      setError('Duration must be at least 0.1 seconds');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onInsert(startTime, endTime, text);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to insert row');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseCurrentTime = () => {
    setStartTime(currentPlaybackTime);
    setEndTime(currentPlaybackTime + 2);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal show d-block" 
      tabIndex="-1" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Insert New {type === 'english_transcript' ? 'English Transcript' : 'Transcript'} Row
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              disabled={isSubmitting}
            />
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger py-2 mb-3">
                  {error}
                </div>
              )}
              
              <div className="mb-3">
                <label className="form-label">Timecodes</label>
                <div className="d-flex align-items-center gap-2">
                  <div>
                    <small className="text-muted d-block mb-1">Start</small>
                    <TimecodeInput
                      value={startTime}
                      onChange={setStartTime}
                    />
                  </div>
                  <span className="mt-4">→</span>
                  <div>
                    <small className="text-muted d-block mb-1">End</small>
                    <TimecodeInput
                      value={endTime}
                      onChange={setEndTime}
                    />
                  </div>
                  {currentPlaybackTime > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-4"
                      onClick={handleUseCurrentTime}
                      title="Use current playback time"
                    >
                      📍 Use Current
                    </button>
                  )}
                </div>
                <small className="text-muted mt-1 d-block">
                  Duration: {(endTime - startTime).toFixed(2)}s
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Text Content</label>
                <textarea
                  className="form-control"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  placeholder="Enter transcript text..."
                  autoFocus
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Inserting...' : 'Insert Row'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InsertRowModal;
